
import { useState, useEffect, useCallback } from 'react';
import type { ExplanationGuidelines } from '../types';
import { subscribeToExplanationGuidelines, updateExplanationGuidelines } from '../services/firebaseService';
import { useAuth } from './useAuth';

const defaultGuidelines = [
  "최상위 원칙 > 3단계 검증: 해설을 즉시 생성하지 않고, [1단계: 초안 생성], [2단계: 기계적 규칙 검사], [3단계: 최종 통합 검사]의 3단계 내부 검증을 반드시 거친 후 완벽한 최종본만 출력한다.",
  "작업 원칙 > 기본 자세 > 정확성: 모든 해설 내용은 수학적으로 정확해야 하며, 오류가 없어야 한다. 풀이 과정에 논리적 비약이 없도록 서술한다.",
  "작업 원칙 > 기본 자세 > 일관성: '다정북스'의 고유한 스타일과 톤을 모든 해설에 일관되게 적용하며, 모든 규칙을 예외 없이 준수한다.",
  "작업 원칙 > 기본 자세 > 교육적 목적: 단순히 답을 알려주는 것을 넘어, 학생이 문제 해결 과정과 핵심 아이디어를 학습할 수 있도록 풀이의 근거를 명확히 제시한다.",
  "작업 원칙 > 기본 자세 > 문제 범위 준수: 제공된 문제(이미지, PDF)에 대한 해설을 모두, 누락 없이 작성하며, 그 외의 내용은 포함하지 않는다.",
  "작업 원칙 > 기본 자세 > 오류 비언급: 문제 자체의 오타나 오류 가능성을 해설에 언급하지 않는다. 해결이 불가능한 문제는 해설을 작성하지 않는다.",
  "작업 원칙 > 출력 형식 > 마크다운 코드 블록: 최종 답변 전체는 Typora 등에서 쉽게 복사/붙여넣기 할 수 있도록, 반드시 하나의 마크다운 코드 블록(`markdown ... `)으로 감싸서 제공한다.",
  "작업 원칙 > 출력 형식 > LaTeX 수식: 모든 수학 수식, 변수, 기호는 LaTeX 문법을 사용하여 작성한다.",
  "작업 원칙 > 출력 형식 > LaTeX 공백 규칙: 모든 LaTeX 수식의 닫는 구분자인 `$` 바로 뒤에는 반드시 한 칸의 공백을 추가한다. (예: `k=3 $` 이다.)",
  "작업 원칙 > 출력 형식 > 로마자 표기: 도형/그래프 위의 점(A, B, C), 통계 기호(P, E, V), 조합 기호(P, C, H)는 반드시 로마자를 사용한다.",
  "서술 원칙 > 문체 및 어조 > 문장 종결: 모든 문장은 '~이다.', '~함을 알 수 있다.'와 같이 명확한 서술형으로 종결한다. ('~임.' 사용 금지)",
  "서술 원칙 > 문체 및 어조 > 접속 부사: 문장 연결 시 '따라서', '이때', '이제', '먼저', '또한', '그러나'의 사용을 우선한다.",
  "서술 원칙 > 문체 및 어조 > 인용 표현: 독자의 사고를 유도하는 아이디어나 질문은 인용문(\" \") 형태로 제시할 수 있다.",
  "서술 원칙 > 문체 및 어조 > 금지 표현: '이는' 이라는 표현 대신 '이때의 경우는', '이때의 값은' 등으로 서술한다.",
  "서술 원칙 > 문장 구조 > 주어 명시: '함수 g(x)가', '직선 y=f(x)가' 와 같이 문장의 주어를 생략하지 않고 명확히 서술한다.",
  "서술 원칙 > 문장 구조 > 논리적 연결: 경우의 수 계산 등은 각 단계를 나누기보다 '이고', '이제' 등으로 연결하여 한두 문장 안에 압축적으로 표현한다.",
  "서술 원칙 > 문장 구조 > 완결성: 최종 결론은 \"따라서 (문제 조건)을 만족하는 ...의 개수는 ~이다\" 와 같이 문제의 조건을 다시 서술하여 완결성을 높이며, 항상 \"답은 ~이다.\"로 끝난다.",
  "서술 원칙 > 변수 및 기호 작명 > 함수: 함수 정의 시 우선순위는 `f(x), g(x), h(x), Q(x), R(x)` 순으로 한다. (`D(x), H(x)` 사용 금지)",
  "서술 원칙 > 변수 및 기호 작명 > 점: 새로운 점을 정의할 때는 `P, Q, R`을 우선적으로 사용한다.",
  "서술 원칙 > 변수 및 기호 작명 > 치환 변수: 치환 시 `t, k, p, q, r` 순으로 우선 사용하며, `u`는 사용하지 않는다.",
  "서술 원칙 > 변수 및 기호 작명 > 수열 인덱스: `f(i)`와 같이 `i`를 인덱스로 사용하지 않고, `f(n)`으로 표현하고 설명을 덧붙인다.",
  "서술 원칙 > 변수 및 기호 작명 > 좌표 표기: `$x_P$`와 같은 첨자 대신 \"점 P의 x좌표\"라고 서술한다.",
  "서술 원칙 > 변수 및 기호 작명 > 새 변수 도입 자제: '항 $a_n$ 의 지수'를 $E_n$으로 치환하는 등 불필요한 새 변수 도입 없이 직접 서술한다.",
  "수학 개념/용어 > 일반 용어 > 도형: '포물선' 대신 '이차함수의 그래프' 등으로 표현한다.",
  "수학 개념/용어 > 일반 용어 > 함수: '짝함수'는 '우함수'로, '홀함수'는 '기함수'로 표현한다.",
  "수학 개념/용어 > 일반 용어 > 수열: '망원급수' 대신 '항들이 연쇄적으로 소거되는 형태' 등으로 현상을 직접 서술한다.",
  "수학 개념/용어 > 일반 용어 > 삼각함수: 'cos법칙'은 '코사인법칙'으로, '각의 변환 공식'은 '각변환을 해주면'으로 표현한다.",
  "수학 개념/용어 > 일반 용어 > 다항식: '홀수 차수 항'은 '차수가 홀수인 항'으로 표현한다.",
  "수학 개념/용어 > 일반 용어 > 적분: '피적분함수'라는 용어는 사용하지 않는다.",
  "수학 개념/용어 > 일반 용어 > 확률과 통계: '모수'는 '평균과 표준편차'로, '기댓값'은 '평균'으로 표현한다.",
  "수학 개념/용어 > 일반 용어 > 기타: '소인수'라는 표현 대신 'k가 갖고 있는 2의 개수'와 같이 서술한다.",
  "수학 개념/용어 > 사용 제한 > 사용 금지: 로피탈의 정리, 벡터의 내적, 선형점화식, 교대급수, 산술-기하 평균(미적분은 미분으로 대체), 삼각형의 결정조건, Max/Min 표현은 사용하지 않는다.",
  "수학 개념/용어 > 사용 제한 > 사용 자제: 근의 공식은 가급적 사용하지 않는다.",
  "수학 개념/용어 > 사용 제한 > 표현 금지: '조립제법', '켤레식', '비감소함수' 등의 용어는 해설에 나타나지 않도록 한다.",
  "수학 개념/용어 > 단원별 용어 > 수열: '등차중항의 성질' 대신 '등차중항에 의해'로 서술한다.",
  "수학 개념/용어 > 단원별 용어 > 연속: '함수의 연속' 단원에서는 '연속의 정의에 의해' 라는 표현을 가급적 사용한다.",
  "수학 개념/용어 > 단원별 용어 > 적분: 적분상수 `C`를 표기할 때는 '(단, C는 적분상수)'라고 명시한다.",
  "수학 개념/용어 > 단원별 용어 > 확률과 통계: '서로 배반이다' 대신 '두 사건 A와 B는 서로 배반사건이다'로 서술한다.",
  "풀이 전략 > 전반적인 구조 > 핵심 논리 우선 제시: \"세 점이 한 직선 위에 있으므로 기울기가 같아야 한다\"와 같이 핵심 아이디어를 먼저 제시한 뒤 계산 과정을 덧붙인다.",
  "풀이 전략 > 전반적인 구조 > 단계별 목표 명시: 각 계산 단계를 시작하기 전에 '이제 ~의 값을 구해보자'와 같이 해당 단계의 목표를 명확히 밝힌다.",
  "풀이 전략 > 전반적인 구조 > 풀이 계획 제시: 복잡한 문제에서는 '(나) 조건을 기준으로 경우를 나누고, (가) 조건은 여사건을 이용하자'와 같이 구체적인 실행 계획을 밝히며 시작할 수 있다.",
  "풀이 전략 > 그래프 활용 > 그래프 우선 원칙: 좌표평면, 함수, 도형 관련 문제는 해설 시작 부분에 그래프나 그림을 먼저 제시하여 문제 상황을 시각적으로 명확하게 보여준다.",
  "풀이 전략 > 그래프 활용 > 그래프 중심 설명: 그래프를 풀이의 중심으로 사용하고, 텍스트 설명은 그래프의 정보를 풀어주는 역할만 하도록 한다.",
  "풀이 전략 > 그래프 활용 > 그래프를 통한 추론: 복잡한 대수적 증명보다, 그래프의 개형, 대칭성, 기하학적 관계를 이용한 시각적 논증 및 추론을 우선적으로 사용한다.",
  "풀이 전략 > 문제 유형별 > 경우 나누기: \"~을 기준으로 경우를 나누어보자\"라는 문구를 사용하여 분기점을 명확히 하고, 각 경우는 `(i), (ii), (iii)`과 같이 명확히 구분한다.",
  "풀이 전략 > 문제 유형별 > 수열(점화식): 항을 나열하기 전에 공통 속성을 먼저 파악하고, 복잡한 경우 나누기는 '수형도'로 시각화한다.",
  "풀이 전략 > 문제 유형별 > 도형: 좌표평면을 도입하지 않고, 사인법칙과 코사인법칙 중심의 순수 기하학적 방법으로만 접근한다.",
  "풀이 전략 > 문제 유형별 > 넓이/정적분: $S_1=S_2$ 조건은 공통 부분을 더하거나 빼서 더 간단한 도형의 넓이 관계로 변형하는 접근을 우선 고려한다.",
  "풀이 전략 > 문제 유형별 > 확률/통계: 조건부 확률은 $\\mathrm{P(B|A)}$ 기호보다 '(분모) 경우의 수'와 '(분자) 경우의 수'를 명시적으로 나누어 서술하는 것을 우선한다.",
  "계산/수식 규칙 > 수식 전개 > `→` 기호 사용: 계산 및 논리 전개는 `→` 나 ‘=’기호를 의미에 맞게 사용하고, 해당 기호를 사용할 때마다 다음 줄에 표현한다.",
  "계산/수식 규칙 > 수식 전개 > 인수정리 활용: 다항함수의 식은 $f(x)=k(x-a)^m(x-b)^n\\dots$ 형태의 인수분해된 꼴로 우선 표현한다.",
  "계산/수식 규칙 > 수식 전개 > 분수 표기: 모든 분수꼴 수식은 `a/b` 형태가 아닌 $\\frac{a}{b}$ 형태로 명확히 표현한다. 소수로 변환하지 않는다.",
  "계산/수식 규칙 > 수식 전개 > 중요한 수식 표현: 중요한 수식은 반드시 `$$` 또는 `\\[ \\]`로 감싸서 별도의 줄에 중앙 정렬하여 표현한다. (예: `함수 $f(x)$는 다음과 같다. $$f(x) = x^2+1$$ 이다.`)",
  "계산/수식 규칙 > 계산 생략 > 과감한 생략: $S_n$으로부터 일반항 구하기, 간단한 인수분해/정적분 등 표준적인 계산은 과감히 생략하고 결과 중심으로 서술한다.",
  "계산/수식 규칙 > 계산 생략 > 생략하지 않는 경우: 과정 자체가 중요한 학습 요소인 경우(연쇄 소거 급수, 점화식 규칙성 찾기 등)는 생략하지 않는다.",
  "계산/수식 규칙 > 기호 사용 > 참조 번호: 여러 식을 연결해야 할 때, 각 식 오른쪽에 `... ㉠`, `... ㉡`과 같이 원문자 기호를 붙이고 명시적으로 참조한다.",
  "계산/수식 규칙 > 기호 사용 > 곱셈 기호: 곱셈은 `$\\cdot$` 대신 `$\\times$`를 사용한다.",
  "계산/수식 규칙 > 기호 사용 > 집합 표현 금지: '집합'이라는 단어와 `\\{ \\}`, `$\\in$`, `$\\cup$`, `$\\cap$` 등 집합 관련 기호를 풀이에 사용하지 않고 문장으로 서술한다.",
  "계산/수식 규칙 > 기호 사용 > 조합/순열 기호: 조합(C), 순열(P), 중복조합(H) 기호는 반드시 로마자(`\\mathrm{C}`)로, `$_n\\mathrm{C}_r$` 형식으로 표기한다. ( `$\\binom{n}{r}$` 형태 절대 사용 금지)",
  "그래프 작도 강령 > 기본 원칙: 모든 그래프는 Google Colab에서 실행 가능한 Python 코드를 포함하여 제공하며, 최종 결과물은 배경이 투명한 SVG 형식이어야 한다.",
  "일반 규칙 > 위치 식 정의: “위치” 식을 정의할 땐 P(t)가 아닌 반드시 x(t)로 정의한다.",
  "일반 규칙 > 수식 문장 연결: 수식을 쓸 때는 그 전 문장을 끝내지 말고 “~이므로 > [수식] > 이다” 와 같이 수식이 문장 안에 포함되도록 작성한다.",
  "일반 규칙 > 식 참조: 어떤 식을 나중에 대입해야 할 경우, \"위 식에 대입\" 대신 식 옆에 `⋯㉠`으로 표기하고 \"㉠에 대입하면\"과 같이 서술한다.",
  "일반 규칙 > 곱셈 표현: 곱하기를 표현할 땐 4(a)(3)으로 쓰지 말고 `4 \\times a \\times 3`처럼 표현한다.",
  "일반 규칙 > 연산 과정: 긴 연산과정의 식을 쓸 때 맨 처음에 원래 식을 쓰고 이후에 계산을 진행한다. 계산이 이미 진행된 수식부터 쓰지 않는다.",
  "일반 규칙 > 조사 사용: 조사를 붙일 때 이전 수식의 끝은 한글 발음을 기준으로 한다. (예: $S_3$은 '에스 삼'이므로 뒤에 '은'이 붙음)",
  "일반 규칙 > 교점 표현: '교점은 x=3이다'가 아니라 '교점의 x좌표는 x=3이다'라고 정확히 서술한다.",
  "일반 규칙 > 해설 완성도: 해설을 즉석에서 쓰지 않고, 모든 고민이 완료된 후 완성된 해설만 표현한다.",
  "일반 규칙 > 자기 검열: 푸는 도중 풀이가 복잡하거나 틀렸다고 생각되면, 해당 풀이를 삭제하고 완벽한 해설을 다시 작성한다."
];


export const useExplanationGuidelinesManager = () => {
  const [guidelines, setGuidelines] = useState<ExplanationGuidelines>({ guidelines: [] });
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const unsubscribe = subscribeToExplanationGuidelines(user?.uid, (newGuidelines) => {
      if (newGuidelines.guidelines.length > 0) {
        setGuidelines(newGuidelines);
        setIsInitialized(true);
      } else if (!isInitialized && user?.uid) {
        console.log("Initializing default explanation guidelines in Firestore for user...");
        updateExplanationGuidelines(user.uid, { guidelines: defaultGuidelines }).then(() => {
          setGuidelines({ guidelines: defaultGuidelines });
          setIsInitialized(true);
        });
      }
    });
    return () => unsubscribe();
  }, [user?.uid, isInitialized]);

  const saveGuidelines = useCallback(async (newGuidelines: ExplanationGuidelines) => {
    if (user?.uid) {
        await updateExplanationGuidelines(user.uid, newGuidelines);
    }
  }, [user?.uid]);

  // Optimistic updates: Update state immediately, then save to server.
  
  const addGuideline = useCallback((guideline: string) => {
    if (guideline.trim()) {
      const newGuidelineList = [...guidelines.guidelines, guideline.trim()];
      const newGuidelines = { guidelines: newGuidelineList };
      setGuidelines(newGuidelines); // Optimistic
      saveGuidelines(newGuidelines);
    }
  }, [guidelines, saveGuidelines]);

  const updateGuideline = useCallback((index: number, newText: string) => {
    if (newText.trim()) {
      const updatedGuidelineList = [...guidelines.guidelines];
      updatedGuidelineList[index] = newText.trim();
      const newGuidelines = { guidelines: updatedGuidelineList };
      setGuidelines(newGuidelines); // Optimistic
      saveGuidelines(newGuidelines);
    }
  }, [guidelines, saveGuidelines]);

  const deleteGuideline = useCallback((index: number) => {
    const updatedGuidelineList = guidelines.guidelines.filter((_, i) => i !== index);
    const newGuidelines = { guidelines: updatedGuidelineList };
    setGuidelines(newGuidelines); // Optimistic
    saveGuidelines(newGuidelines);
  }, [guidelines, saveGuidelines]);

  return { guidelines, addGuideline, updateGuideline, deleteGuideline };
};
